import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Plus, RefreshCcw, Trash2, X, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription } from '@easyflow/ui-frontend/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { apiClient as api } from '@/lib/api';
import type { ApiKeyWire, ApiKeyWithRawKey } from '@easyflow/shared';
import { useToast } from '@/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';

interface PublicKey {
  id: string;
  name: string;
  prefix: string;
  allowed_origins: string[];
  allowed_form_ids: string[];
  last_used_at: string | null;
  created_at: string;
}

export interface PublicKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  formName: string;
  formSlug: string;
}

/**
 * Task #507 — origins are stored as full Origin strings (`scheme://host[:port]`)
 * and matched against the browser's `Origin` header server-side. We therefore
 * accept only:
 *   - https://… (any host) — production embeds
 *   - http://localhost[:port] / http://127.0.0.1[:port] — local development
 * Anything else (bare hostnames, paths, plain `http://example.com`) is rejected
 * with an inline error so the chip never silently corrupts the allowlist.
 */
function normalizeOrigin(raw: string): { ok: true; value: string } | { ok: false; error: string } {
  const cleaned = raw.trim().toLowerCase();
  if (!cleaned) return { ok: false, error: 'Origin vuoto' };
  let url: URL;
  try {
    url = new URL(cleaned);
  } catch {
    return { ok: false, error: `Origin non valido: "${raw}". Usa il formato https://dominio.it` };
  }
  if (url.pathname !== '/' && url.pathname !== '') {
    return { ok: false, error: `Origin "${raw}" deve contenere solo schema + host (niente percorso).` };
  }
  const isLocal = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  if (url.protocol === 'https:' || (url.protocol === 'http:' && isLocal)) {
    const port = url.port ? `:${url.port}` : '';
    return { ok: true, value: `${url.protocol}//${url.hostname}${port}` };
  }
  return { ok: false, error: `Origin "${raw}" deve usare https:// (oppure http://localhost per sviluppo).` };
}

function OriginsChipInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const commit = (raw: string) => {
    const parts = raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const errors: string[] = [];
    const accepted: string[] = [];
    for (const p of parts) {
      const r = normalizeOrigin(p);
      if (r.ok) accepted.push(r.value);
      else errors.push((r as { ok: false; error: string }).error);
    }
    if (accepted.length > 0) {
      onChange(Array.from(new Set([...value, ...accepted])));
    }
    if (errors.length > 0) {
      setError(errors.join(' '));
    } else {
      setError(undefined);
      setDraft('');
    }
    if (accepted.length > 0 && errors.length === 0) setDraft('');
  };
  return (
    <div>
      <div className="min-h-[44px] rounded-md border bg-background px-2 py-1.5 flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring">
        {value.map((o) => (
          <span key={o} className="inline-flex items-center gap-1 bg-muted text-xs px-2 py-1 rounded-full">
            <code className="font-mono text-[11px]">{o}</code>
            <button
              type="button"
              className="hover:text-destructive"
              aria-label={`Rimuovi ${o}`}
              onClick={() => onChange(value.filter((x) => x !== o))}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(undefined); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit(draft);
            } else if (e.key === 'Backspace' && !draft && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => draft && commit(draft)}
          placeholder={value.length ? '' : 'https://www.miosito.it'}
          className="flex-1 min-w-[140px] bg-transparent outline-none text-sm py-1"
        />
      </div>
      {error ? (
        <p className="text-[11px] text-red-500 mt-1">{error}</p>
      ) : (
        <p className="text-[11px] text-muted-foreground mt-1">
          Premi Invio o virgola per aggiungere. Solo <code>https://…</code> oppure <code>http://localhost</code> per sviluppo.
        </p>
      )}
    </div>
  );
}

export default function PublicKeyDialog({ open, onOpenChange, formId, formName, formSlug }: PublicKeyDialogProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [editingOrigins, setEditingOrigins] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [newOrigins, setNewOrigins] = useState<string[]>([]);
  const [revealedRaw, setRevealedRaw] = useState<string | undefined>(undefined);

  const { data: keys, refetch } = useQuery<PublicKey[]>({
    queryKey: ['public-api-keys', formId],
    enabled: open && !!formId,
    queryFn: async () => {
      const res = await api.get<ApiKeyWire[]>(`/api-keys?formId=${formId}`);
      // Defensive double-unwrap: `res.data` is already the keys array, but a
      // legacy double-envelope (`{ data: { data: [...] } }`) would land here as
      // an object with its own `.data`; keep the runtime tolerant of both.
      const d: unknown = res.data;
      const list = Array.isArray(d) ? d : ((d as { data?: ApiKeyWire[] } | null)?.data ?? []);
      return list as PublicKey[];
    },
  });

  // Auto-select first key
  useEffect(() => {
    if (!open) return;
    if (keys && keys.length > 0 && !selectedId && keys[0]) setSelectedId(keys[0].id);
  }, [open, keys, selectedId]);

  const selected = useMemo(() => keys?.find((k) => k.id === selectedId), [keys, selectedId]);

  // Sync editingOrigins when selection changes
  useEffect(() => {
    setEditingOrigins(selected?.allowed_origins || []);
  }, [selected?.id, selected?.allowed_origins]);

  useEffect(() => {
    if (!open) {
      setRevealedRaw(undefined);
      setNewName('');
      setNewOrigins([]);
      setSelectedId(undefined);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: async () => {
      const name = newName.trim() || `${formName} — Sito`;
      const res = await api.post<ApiKeyWithRawKey>('/api-keys', {
        name,
        keyType: 'public',
        scopes: ['calendar:read', 'calendar:write'],
        allowedFormIds: [formId],
        allowedOrigins: newOrigins,
      });
      const d: unknown = res.data;
      const key = (d && typeof d === 'object' && 'data' in d && !('id' in d))
        ? (d as { data: ApiKeyWithRawKey }).data
        : (d as ApiKeyWithRawKey);
      return key as PublicKey & { raw_key: string };
    },
    onSuccess: (data) => {
      setRevealedRaw(data.raw_key);
      setSelectedId(data.id);
      setNewName('');
      setNewOrigins([]);
      qc.invalidateQueries({ queryKey: ['public-api-keys', formId] });
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast({ title: 'Chiave creata', description: 'Copia subito il valore: non sarà più visibile.' });
    },
    onError: () => toast({ title: 'Errore', description: 'Impossibile creare la chiave', variant: 'destructive' }),
  });

  const rotate = useMutation({
    mutationFn: async (id: string) => {
      // Task #507 — when rotating from the per-form dialog we forcibly rebind
      // the new key to *only* this form. This prevents legacy "broad" keys
      // (allowed_form_ids = []) from staying broad after a rotate triggered
      // from a single form's "Chiave pubblica" panel.
      const res = await api.post<ApiKeyWithRawKey>(`/api-keys/${id}/rotate`, {
        allowedFormIds: [formId],
      });
      const d: unknown = res.data;
      const key = (d && typeof d === 'object' && 'data' in d && !('id' in d))
        ? (d as { data: ApiKeyWithRawKey }).data
        : (d as ApiKeyWithRawKey);
      return key as PublicKey & { raw_key: string };
    },
    onSuccess: (data) => {
      setRevealedRaw(data.raw_key);
      setSelectedId(data.id);
      qc.invalidateQueries({ queryKey: ['public-api-keys', formId] });
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast({
        title: 'Chiave rigenerata',
        description: 'La vecchia chiave non funziona più. Aggiorna gli embed con la nuova.',
      });
    },
    onError: () => toast({ title: 'Errore', description: 'Impossibile rigenerare la chiave', variant: 'destructive' }),
  });

  const updateOrigins = useMutation({
    mutationFn: async (vars: { id: string; origins: string[] }) => {
      await api.patch(`/api-keys/${vars.id}`, {
        allowedFormIds: [formId],
        allowedOrigins: vars.origins,
      });
      return vars.origins;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public-api-keys', formId] });
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast({ title: 'Origins aggiornate' });
      refetch();
    },
    onError: () => toast({ title: 'Errore', description: 'Impossibile aggiornare le origins', variant: 'destructive' }),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api-keys/${id}`);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['public-api-keys', formId] });
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      if (selectedId === id) setSelectedId(undefined);
      toast({ title: 'Chiave revocata' });
    },
    onError: () => toast({ title: 'Errore', description: 'Impossibile revocare', variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> Chiave pubblica — {formName}
          </DialogTitle>
          <DialogDescription>
            Le chiavi pubbliche (<code className="text-xs">pk_live_…</code>) abilitano il widget incorporato a leggere
            disponibilità e creare prenotazioni dal browser. Sono vincolate a questo form e (consigliato) a un elenco di
            domini autorizzati.
          </DialogDescription>
        </DialogHeader>

        {revealedRaw && (
          <Alert className="border-amber-500 bg-amber-500/10">
            <AlertDescription className="text-xs space-y-2">
              <div className="font-medium text-amber-900 dark:text-amber-200">
                Salva questa chiave: non sarà più visibile dopo aver chiuso la finestra.
              </div>
              <div className="flex items-center gap-2 bg-background rounded px-2 py-1.5">
                <code className="flex-1 break-all text-[11px]">{revealedRaw}</code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(revealedRaw);
                    toast({ title: 'Copiato' });
                  }}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {keys && keys.length > 0 && (
          <div className="space-y-3 rounded-md border p-3">
            <Label className="text-xs uppercase text-muted-foreground">Chiavi esistenti per questo form</Label>
            <Select value={selectedId || ''} onValueChange={(v) => { setSelectedId(v); setRevealedRaw(undefined); }}>
              <SelectTrigger className="min-h-[44px]">
                <SelectValue placeholder="Seleziona una chiave" />
              </SelectTrigger>
              <SelectContent>
                {keys.map((k) => (
                  <SelectItem key={k.id} value={k.id}>
                    {k.name} — <code>{k.prefix}…</code>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selected && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="font-mono">{selected.prefix}…</Badge>
                  {selected.last_used_at ? (
                    <span>Ultimo uso: {new Date(selected.last_used_at).toLocaleString()}</span>
                  ) : (
                    <span>Mai usata</span>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Label>Origins consentiti</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground">
                            <Info className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="text-xs">
                            Domini autorizzati a usare questa chiave nel browser (controllo Origin).
                            Vuoto = qualsiasi origine (sconsigliato in produzione).
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <OriginsChipInput value={editingOrigins} onChange={setEditingOrigins} />
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={updateOrigins.isPending}
                      onClick={() => updateOrigins.mutate({ id: selected.id, origins: editingOrigins })}
                    >
                      {updateOrigins.isPending ? 'Salvataggio...' : 'Salva origins'}
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rotate.isPending}
                    onClick={() => {
                      if (!window.confirm('Rigenerare la chiave? La precedente smetterà immediatamente di funzionare.')) return;
                      rotate.mutate(selected.id);
                    }}
                  >
                    <RefreshCcw className="w-3 h-3 mr-1" />
                    {rotate.isPending ? 'Rigenero...' : 'Rigenera'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={revoke.isPending}
                    onClick={() => {
                      if (!window.confirm('Revocare definitivamente questa chiave? Tutti gli embed che la usano smetteranno di funzionare.')) return;
                      revoke.mutate(selected.id);
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    {revoke.isPending ? 'Revoca...' : 'Revoca'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2 rounded-md border p-3">
          <Label className="text-xs uppercase text-muted-foreground">Genera una nuova chiave</Label>
          <Input
            placeholder={`Nome (es. ${formName} — sito vetrina)`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="min-h-[44px]"
          />
          <OriginsChipInput value={newOrigins} onChange={setNewOrigins} />
          <Button
            size="sm"
            disabled={create.isPending}
            onClick={() => create.mutate()}
          >
            <Plus className="w-3 h-3 mr-1" />
            {create.isPending ? 'Creo...' : 'Genera chiave pubblica'}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            La chiave sarà legata esclusivamente al form <code>{formSlug}</code>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
